import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../../../utils/api";
import {
  Card,
  Button,
  PageHeader,
  AdminLayout,
} from "../components/AdminSharedComponents";
import { ChevronLeft, Edit, Image } from "lucide-react";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/api/products/${id}`);
        setProduct(response.data);
      } catch (error) {
        setError("Product not found");
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) {
    return (
      <AdminLayout>
        <PageHeader
          title="Product Details"
          subtitle="Loading product information"
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-primary-500 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 mr-3 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="text-lg font-medium">
              Loading product details...
            </span>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <PageHeader title="Product Details" subtitle="Error" />
        <Card className="bg-red-50 border border-red-200 text-red-700 text-center p-6 rounded-lg">
          <div className="text-2xl font-semibold mb-2">Oops!</div>
          <p className="text-base">{error}</p>
        </Card>
      </AdminLayout>
    );
  }

  if (!product) {
    return (
      <AdminLayout>
        <PageHeader title="Product Details" subtitle="No Data" />
        <Card className="bg-gray-50 border border-gray-200 text-gray-700 text-center p-6 rounded-lg">
          <div className="text-2xl font-semibold mb-2">No Product Found</div>
          <p className="text-base">The requested product does not exist.</p>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-6">
        <PageHeader
          title="Product Details"
          subtitle={product.title}
          actions={
            <Button
              variant="outline"
              onClick={() => (window.location.href = "/admin/products")}
              className="flex items-center gap-2 hover:bg-gray-100 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Products
            </Button>
          }
        />

        <div className="grid md:grid-cols-3 gap-6">
          {/* Left Column - Product Image */}
          <div className="md:col-span-1">
            <Card className="p-4 bg-white shadow-sm">
              {product.image_path ? (
                <div className="flex justify-center items-center h-64 bg-gray-50 rounded-lg overflow-hidden">
                  <img
                    src={`${process.env.REACT_APP_API_URL}${product.image_path}`}
                    alt={product.title}
                    className="max-h-full max-w-full object-contain rounded"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src =
                        "https://via.placeholder.com/300?text=No+Image";
                    }}
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center h-64 bg-gray-100 rounded-lg">
                  <Image className="h-16 w-16 text-gray-400" />
                  <span className="ml-2 text-gray-500">No image available</span>
                </div>
              )}
            </Card>
          </div>

          {/* Right Column - Product Details */}
          <div className="md:col-span-2">
            <Card className="p-6 space-y-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-800 mb-2">
                  {product.title}
                </h1>
                {product.detail_page && (
                  <a
                    href={product.detail_page}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
                  >
                    View Detail Page
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                  </a>
                )}
              </div>

              {/* Description Section */}
              <div>
                <h2 className="text-lg font-semibold text-gray-700 mb-2">
                  Description
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {product.description || "No description available"}
                </p>
              </div>

              {/* Detailed Sections with Consistent Styling */}
              {[
                {
                  title: "Features",
                  data: product.features,
                  key: "feature_name",
                  emptyMessage: "No features available",
                },
                {
                  title: "Specifications",
                  data: product.specifications,
                  key: "spec_name",
                  isTable: true,
                  emptyMessage: "No specifications available",
                },
                {
                  title: "Certifications",
                  data: product.certifications,
                  key: "certification_name",
                  emptyMessage: "No certifications available",
                },
                {
                  title: "Testing Capabilities",
                  data: product.testingCapabilities,
                  key: "capability_name",
                  emptyMessage: "No testing capabilities available",
                },
                {
                  title: "Validation Methods",
                  data: product.validationMethods,
                  key: "method_name",
                  emptyMessage: "No validation methods available",
                },
              ].map(({ title, data, key, isTable, emptyMessage }) => (
                <div key={title} className="mt-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-2">
                    {title}
                  </h2>
                  {data && data.length > 0 ? (
                    isTable ? (
                      <div className="bg-gray-50 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-200">
                            <tr>
                              <th className="px-4 py-2 text-left">
                                Specification
                              </th>
                              <th className="px-4 py-2 text-left">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.map((item, index) => (
                              <tr
                                key={index}
                                className="border-b last:border-b-0 hover:bg-gray-100"
                              >
                                <td className="px-4 py-2">{item[key]}</td>
                                <td className="px-4 py-2">
                                  {item.spec_value || item[key]}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <ul className="space-y-1 text-gray-600 pl-4">
                        {data.map((item, index) => (
                          <li key={index} className="flex items-center">
                            <span className="mr-2 text-blue-500">•</span>
                            {item[key]}
                          </li>
                        ))}
                      </ul>
                    )
                  ) : (
                    <p className="text-gray-500 italic">{emptyMessage}</p>
                  )}
                </div>
              ))}

              {/* Actions */}
              <div className="mt-6 flex space-x-4">
                <Button
                  variant="secondary"
                  className="flex-1 py-2 text-center flex items-center justify-center gap-2"
                  onClick={() =>
                    (window.location.href = `/admin/products/edit/${product.id}`)
                  }
                >
                  <Edit className="h-4 w-4" />
                  Edit Product
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ProductDetail;
